import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { Add, Assignment, GroupAdd, PersonRemove, Star } from '@mui/icons-material';
import axios from 'axios';
import dayjs from 'dayjs';
import { useToast } from '../../contexts/ToastContext';

const TASK_STATUSES = ['OPEN', 'IN_PROGRESS', 'DONE', 'CANCELLED'];

function employeeLabel(user) {
  if (!user) return '—';
  return `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || '—';
}

/**
 * HRMS Groups — HR + department managers create groups, set leads,
 * assign members (multi-group), and assign group tasks.
 */
function GroupsPanel({
  employees = [],
  canManageGroups,
  department,
  departments = [],
  managedDepartments = null
}) {
  const { showToast } = useToast();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);
  const [tasksOpen, setTasksOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupTasks, setGroupTasks] = useState([]);
  const [createForm, setCreateForm] = useState({
    name: '',
    code: '',
    department: '',
    description: ''
  });
  const [memberUserId, setMemberUserId] = useState('');
  const [memberRemarks, setMemberRemarks] = useState('');
  const [leadUserId, setLeadUserId] = useState('');
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    assigneeIds: [],
    dueDate: '',
    remarks: ''
  });
  const [saving, setSaving] = useState(false);

  const departmentOptions = useMemo(() => {
    if (Array.isArray(managedDepartments) && managedDepartments.length) {
      return managedDepartments;
    }
    return departments;
  }, [departments, managedDepartments]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { activeOnly: 'false' };
      if (department) params.department = department;
      const res = await axios.get('/api/hrms/groups', { params });
      setGroups(res.data || []);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to load groups', 'error');
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, [department, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    const defaultDept = department
      || (departmentOptions.length === 1 ? departmentOptions[0] : '');
    setCreateForm({
      name: '',
      code: '',
      department: defaultDept,
      description: ''
    });
    setCreateOpen(true);
  };

  const openMembers = async (group) => {
    try {
      const res = await axios.get(`/api/hrms/groups/${group._id}`);
      setSelectedGroup(res.data);
      setMemberUserId('');
      setMemberRemarks('');
      setLeadUserId('');
      setMembersOpen(true);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to load group', 'error');
    }
  };

  const openTasks = async (group) => {
    try {
      const [groupRes, tasksRes] = await Promise.all([
        axios.get(`/api/hrms/groups/${group._id}`),
        axios.get(`/api/hrms/groups/${group._id}/tasks`)
      ]);
      setSelectedGroup(groupRes.data);
      setGroupTasks(tasksRes.data || []);
      setTaskForm({
        title: '',
        description: '',
        assigneeIds: [],
        dueDate: dayjs().add(7, 'day').format('YYYY-MM-DD'),
        remarks: ''
      });
      setTasksOpen(true);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to load group tasks', 'error');
    }
  };

  const onCreate = async () => {
    if (!createForm.name.trim()) {
      showToast('Name is required', 'error');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: createForm.name,
        code: createForm.code || undefined,
        description: createForm.description || undefined,
        department: createForm.department || undefined
      };
      await axios.post('/api/hrms/groups', payload);
      showToast('Group created', 'success');
      setCreateOpen(false);
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Create failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (group) => {
    try {
      await axios.put(`/api/hrms/groups/${group._id}`, { isActive: !group.isActive });
      showToast(group.isActive ? 'Group deactivated' : 'Group activated', 'success');
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Update failed', 'error');
    }
  };

  const eligibleEmployees = useMemo(() => {
    if (!selectedGroup) return [];
    const memberIds = new Set(
      (selectedGroup.members || []).map((m) => String(m.userId?._id || m.userId))
    );
    return employees.filter((e) => !memberIds.has(String(e._id)));
  }, [employees, selectedGroup]);

  const leadIds = useMemo(() => new Set(
    (selectedGroup?.leads || []).map((l) => String(l.userId?._id || l.userId))
  ), [selectedGroup]);

  const eligibleLeads = useMemo(() => {
    if (!selectedGroup) return [];
    return (selectedGroup.members || [])
      .map((m) => m.userId)
      .filter((u) => u && !leadIds.has(String(u._id || u)));
  }, [selectedGroup, leadIds]);

  const onAssignMember = async () => {
    if (!selectedGroup || !memberUserId) {
      showToast('Select an employee', 'error');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`/api/hrms/groups/${selectedGroup._id}/members`, {
        userId: memberUserId,
        remarks: memberRemarks
      });
      setSelectedGroup(res.data);
      setMemberUserId('');
      setMemberRemarks('');
      showToast('Member assigned (employees may be in multiple groups)', 'success');
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Assign failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const onRemoveMember = async (userId) => {
    if (!selectedGroup) return;
    try {
      const res = await axios.delete(
        `/api/hrms/groups/${selectedGroup._id}/members/${userId}`
      );
      setSelectedGroup(res.data);
      showToast('Member removed', 'success');
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Remove failed', 'error');
    }
  };

  const onAssignLead = async () => {
    if (!selectedGroup || !leadUserId) {
      showToast('Select a member to make lead', 'error');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`/api/hrms/groups/${selectedGroup._id}/leads`, {
        userId: leadUserId
      });
      setSelectedGroup(res.data);
      setLeadUserId('');
      showToast('Group lead set', 'success');
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Set lead failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const onRemoveLead = async (userId) => {
    if (!selectedGroup) return;
    try {
      const res = await axios.delete(
        `/api/hrms/groups/${selectedGroup._id}/leads/${userId}`
      );
      setSelectedGroup(res.data);
      showToast('Lead removed', 'success');
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Remove lead failed', 'error');
    }
  };

  const refreshTasks = async (groupId) => {
    const res = await axios.get(`/api/hrms/groups/${groupId}/tasks`);
    setGroupTasks(res.data || []);
  };

  const onCreateTask = async () => {
    if (!selectedGroup) return;
    if (!taskForm.title.trim()) {
      showToast('Task title is required', 'error');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`/api/hrms/groups/${selectedGroup._id}/tasks`, {
        title: taskForm.title,
        description: taskForm.description || undefined,
        assigneeIds: taskForm.assigneeIds.length ? taskForm.assigneeIds : undefined,
        dueDate: taskForm.dueDate || undefined,
        remarks: taskForm.remarks || undefined
      });
      showToast('Task assigned', 'success');
      setTaskForm({
        title: '',
        description: '',
        assigneeIds: [],
        dueDate: dayjs().add(7, 'day').format('YYYY-MM-DD'),
        remarks: ''
      });
      await refreshTasks(selectedGroup._id);
    } catch (err) {
      showToast(err.response?.data?.message || 'Assign task failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const onUpdateTaskStatus = async (taskId, status) => {
    try {
      await axios.put(`/api/hrms/group-tasks/${taskId}`, { status });
      if (selectedGroup) await refreshTasks(selectedGroup._id);
      showToast('Task updated', 'success');
    } catch (err) {
      showToast(err.response?.data?.message || 'Update failed', 'error');
    }
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        HR and department managers create groups, set group leads, and assign tasks.
        Employees may be in multiple groups. Department managers only manage their department groups.
        Leave assignees empty when creating a task to assign to all current members.
      </Alert>

      {!canManageGroups && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You can view groups you belong to and tasks assigned to you.
          Only HR or department managers can create groups, set leads, or assign tasks.
        </Alert>
      )}

      <Stack direction="row" spacing={1} mb={2} flexWrap="wrap">
        {canManageGroups && (
          <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
            Create group
          </Button>
        )}
        <Button onClick={load} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </Button>
      </Stack>

      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Code</TableCell>
              <TableCell>Department</TableCell>
              <TableCell>Members</TableCell>
              <TableCell>Leads</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created by</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.map((group) => (
              <TableRow key={group._id} hover>
                <TableCell>
                  <Typography variant="body2">{group.name}</Typography>
                  {group.description ? (
                    <Typography variant="caption" color="text.secondary">
                      {group.description}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell>{group.code || '—'}</TableCell>
                <TableCell>{group.department || '—'}</TableCell>
                <TableCell>{(group.members || []).length}</TableCell>
                <TableCell>
                  {(group.leads || []).length
                    ? (group.leads || []).map((l) => employeeLabel(l.userId)).join(', ')
                    : '—'}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={group.isActive ? 'Active' : 'Inactive'}
                    color={group.isActive ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell>{employeeLabel(group.createdBy)}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    <Button
                      size="small"
                      startIcon={<GroupAdd />}
                      onClick={() => openMembers(group)}
                    >
                      Members
                    </Button>
                    <Button
                      size="small"
                      startIcon={<Assignment />}
                      onClick={() => openTasks(group)}
                    >
                      Tasks
                    </Button>
                    {canManageGroups && (
                      <Button size="small" onClick={() => toggleActive(group)}>
                        {group.isActive ? 'Deactivate' : 'Activate'}
                      </Button>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {!groups.length && (
              <TableRow>
                <TableCell colSpan={8}>
                  {loading ? 'Loading…' : 'No groups yet — HR or department managers can create them'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create group</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              required
              fullWidth
              value={createForm.name}
              onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
              placeholder="e.g. Night shift pool, Project Alpha"
            />
            <TextField
              label="Code"
              fullWidth
              value={createForm.code}
              onChange={(e) => setCreateForm({ ...createForm, code: e.target.value })}
              placeholder="Optional short code"
            />
            <FormControl fullWidth>
              <InputLabel>
                {Array.isArray(managedDepartments) ? 'Department' : 'Department (optional)'}
              </InputLabel>
              <Select
                label={Array.isArray(managedDepartments) ? 'Department' : 'Department (optional)'}
                value={createForm.department}
                onChange={(e) => setCreateForm({ ...createForm, department: e.target.value })}
              >
                {!Array.isArray(managedDepartments) && <MenuItem value="">None</MenuItem>}
                {departmentOptions.map((d) => (
                  <MenuItem key={d} value={d}>{d}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Description"
              fullWidth
              multiline
              minRows={2}
              value={createForm.description}
              onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={onCreate} disabled={saving}>Create</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={membersOpen} onClose={() => setMembersOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>
          Group members & leads
          {selectedGroup ? ` · ${selectedGroup.name}` : ''}
        </DialogTitle>
        <DialogContent>
          {selectedGroup && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              {canManageGroups && selectedGroup.isActive && (
                <>
                  <Typography variant="subtitle2">
                    Assign employee (may already be in other groups)
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start">
                    <FormControl fullWidth size="small">
                      <InputLabel>Employee</InputLabel>
                      <Select
                        label="Employee"
                        value={memberUserId}
                        onChange={(e) => setMemberUserId(e.target.value)}
                      >
                        {eligibleEmployees.map((e) => (
                          <MenuItem key={e._id} value={e._id}>
                            {e.firstName} {e.lastName}
                            {(e.groups || []).length
                              ? ` · in ${(e.groups || []).map((g) => g.name).join(', ')}`
                              : ''}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      size="small"
                      label="Remarks"
                      value={memberRemarks}
                      onChange={(e) => setMemberRemarks(e.target.value)}
                      sx={{ minWidth: 180 }}
                    />
                    <Button
                      variant="contained"
                      onClick={onAssignMember}
                      disabled={saving || !memberUserId}
                    >
                      Assign
                    </Button>
                  </Stack>
                  <Divider />
                  <Typography variant="subtitle2">
                    Set group lead (HR / department managers choose; must be a member)
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start">
                    <FormControl fullWidth size="small">
                      <InputLabel>Member</InputLabel>
                      <Select
                        label="Member"
                        value={leadUserId}
                        onChange={(e) => setLeadUserId(e.target.value)}
                      >
                        {eligibleLeads.map((u) => (
                          <MenuItem key={u._id || u} value={u._id || u}>
                            {employeeLabel(u)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      variant="outlined"
                      startIcon={<Star />}
                      onClick={onAssignLead}
                      disabled={saving || !leadUserId}
                    >
                      Make lead
                    </Button>
                  </Stack>
                  <Divider />
                </>
              )}

              <Typography variant="subtitle2">
                Leads ({(selectedGroup.leads || []).length})
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Lead</TableCell>
                    <TableCell>Since</TableCell>
                    <TableCell>By</TableCell>
                    {canManageGroups && <TableCell />}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(selectedGroup.leads || []).map((l) => (
                    <TableRow key={l._id || String(l.userId?._id || l.userId)}>
                      <TableCell>{employeeLabel(l.userId)}</TableCell>
                      <TableCell>
                        {l.assignedAt ? dayjs(l.assignedAt).format('YYYY-MM-DD') : '—'}
                      </TableCell>
                      <TableCell>{employeeLabel(l.assignedBy)}</TableCell>
                      {canManageGroups && (
                        <TableCell>
                          <Button
                            size="small"
                            color="error"
                            onClick={() => onRemoveLead(l.userId?._id || l.userId)}
                          >
                            Remove lead
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  {!(selectedGroup.leads || []).length && (
                    <TableRow>
                      <TableCell colSpan={canManageGroups ? 4 : 3}>No leads yet</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              <Typography variant="subtitle2">
                Current members ({(selectedGroup.members || []).length})
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Employee</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Assigned</TableCell>
                    <TableCell>By</TableCell>
                    {canManageGroups && <TableCell />}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(selectedGroup.members || []).map((m) => {
                    const uid = String(m.userId?._id || m.userId);
                    const isLead = leadIds.has(uid);
                    return (
                      <TableRow key={m._id || uid}>
                        <TableCell>
                          {employeeLabel(m.userId)}
                          {isLead ? ' ★' : ''}
                        </TableCell>
                        <TableCell>{isLead ? 'Lead' : 'Member'}</TableCell>
                        <TableCell>
                          {m.assignedAt ? dayjs(m.assignedAt).format('YYYY-MM-DD') : '—'}
                        </TableCell>
                        <TableCell>{employeeLabel(m.assignedBy)}</TableCell>
                        {canManageGroups && (
                          <TableCell>
                            <Button
                              size="small"
                              color="error"
                              startIcon={<PersonRemove />}
                              onClick={() => onRemoveMember(m.userId?._id || m.userId)}
                            >
                              Remove
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                  {!(selectedGroup.members || []).length && (
                    <TableRow>
                      <TableCell colSpan={canManageGroups ? 5 : 4}>No members yet</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMembersOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={tasksOpen} onClose={() => setTasksOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>
          Group tasks
          {selectedGroup ? ` · ${selectedGroup.name}` : ''}
        </DialogTitle>
        <DialogContent>
          {selectedGroup && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              {canManageGroups && selectedGroup.isActive && (
                <>
                  <Typography variant="subtitle2">
                    Assign task (HR: any group · Department manager: own department groups)
                  </Typography>
                  <TextField
                    label="Title"
                    required
                    fullWidth
                    size="small"
                    value={taskForm.title}
                    onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                  />
                  <TextField
                    label="Description"
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                    value={taskForm.description}
                    onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                  />
                  <FormControl fullWidth size="small">
                    <InputLabel id="task-assignees-label">Assignees (optional = all members)</InputLabel>
                    <Select
                      labelId="task-assignees-label"
                      multiple
                      value={taskForm.assigneeIds}
                      onChange={(e) => setTaskForm({ ...taskForm, assigneeIds: e.target.value })}
                      input={<OutlinedInput label="Assignees (optional = all members)" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((id) => {
                            const m = (selectedGroup.members || []).find(
                              (row) => String(row.userId?._id || row.userId) === String(id)
                            );
                            return (
                              <Chip
                                key={id}
                                size="small"
                                label={m ? employeeLabel(m.userId) : id}
                              />
                            );
                          })}
                        </Box>
                      )}
                    >
                      {(selectedGroup.members || []).map((m) => {
                        const id = m.userId?._id || m.userId;
                        return (
                          <MenuItem key={String(id)} value={id}>
                            <Checkbox
                              checked={taskForm.assigneeIds.map(String).includes(String(id))}
                            />
                            <ListItemText primary={employeeLabel(m.userId)} />
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <TextField
                      label="Due date"
                      type="date"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      value={taskForm.dueDate}
                      onChange={(e) => setTaskForm({ ...taskForm, dueDate: e.target.value })}
                      sx={{ minWidth: 180 }}
                    />
                    <TextField
                      label="Remarks"
                      size="small"
                      fullWidth
                      value={taskForm.remarks}
                      onChange={(e) => setTaskForm({ ...taskForm, remarks: e.target.value })}
                    />
                    <Button
                      variant="contained"
                      onClick={onCreateTask}
                      disabled={saving || !taskForm.title.trim()}
                    >
                      Assign task
                    </Button>
                  </Stack>
                  <Divider />
                </>
              )}

              <Typography variant="subtitle2">
                Tasks ({groupTasks.length})
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Title</TableCell>
                    <TableCell>Assignees</TableCell>
                    <TableCell>Due</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>By</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groupTasks.map((task) => (
                    <TableRow key={task._id}>
                      <TableCell>
                        <Typography variant="body2">{task.title}</Typography>
                        {task.description ? (
                          <Typography variant="caption" color="text.secondary">
                            {task.description}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        {(task.assigneeIds || []).map((u) => employeeLabel(u)).join(', ') || '—'}
                      </TableCell>
                      <TableCell>
                        {task.dueDate ? dayjs(task.dueDate).format('YYYY-MM-DD') : '—'}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={task.status.replace(/_/g, ' ')} />
                      </TableCell>
                      <TableCell>{employeeLabel(task.assignedBy)}</TableCell>
                      <TableCell>
                        <FormControl size="small" sx={{ minWidth: 140 }}>
                          <Select
                            value={task.status}
                            onChange={(e) => onUpdateTaskStatus(task._id, e.target.value)}
                          >
                            {TASK_STATUSES.map((s) => (
                              <MenuItem
                                key={s}
                                value={s}
                                disabled={!canManageGroups && s === 'CANCELLED'}
                              >
                                {s.replace(/_/g, ' ')}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!groupTasks.length && (
                    <TableRow>
                      <TableCell colSpan={6}>No tasks yet</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTasksOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default GroupsPanel;
